import {
  extractRolloutTags,
  getTagSearchText,
} from '@/Modules/Core/Components/Tags/tagUtils';

export const PAGE_SIZE_OPTIONS = ['10', '25', '50', '100'];

const DATE_FORMATTER = new Intl.DateTimeFormat('en-US', {
  month: 'short',
  day: 'numeric',
  year: 'numeric',
});

const toTitleCase = (value = '') =>
  String(value)
    .toLowerCase()
    .replace(/\b\w/g, (character) => character.toUpperCase());

export const formatRolloutDate = (value) => {
  if (!value) {
    return 'Unknown date';
  }

  const date = new Date(value);

  if (Number.isNaN(date.getTime())) {
    return 'Unknown date';
  }

  return DATE_FORMATTER.format(date);
};

export const formatRolloutTypeLabel = (value) => {
  const normalized = String(value ?? '')
    .replace(/[_-]+/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();

  if (!normalized) {
    return '';
  }

  const rolloutTypeKey = normalized.toLowerCase();

  if (['accum', 'accumulative', 'cumulative'].includes(rolloutTypeKey)) {
    return 'Accumulative';
  }

  if (['recreate', 'recreated'].includes(rolloutTypeKey)) {
    return 'Recreate';
  }

  return toTitleCase(normalized);
};

export const normalizeRollout = (item, index = 0) => {
  const tags = extractRolloutTags(item);
  const rolloutType = String(
    item?.rollout_type ?? item?.rolloutType ?? ''
  ).trim();
  const children = Array.isArray(item?.children)
    ? item.children.map((child, childIndex) =>
        normalizeRollout(child, childIndex)
      )
    : [];

  return {
    key: item?.rollout_id ?? item?.rollout_name ?? `rollout-${index + 1}`,
    rolloutId: item?.rollout_id ?? null,
    rolloutName: String(item?.rollout_name ?? ''),
    description: String(item?.rollout_description ?? '').trim(),
    environment: String(item?.environment ?? '').trim(),
    rolloutType,
    rolloutTypeLabel: formatRolloutTypeLabel(rolloutType),
    createdAt: item?.created_date ?? '',
    createdAtLabel: formatRolloutDate(item?.created_date),
    totalBugs: Number(item?.total_bugs ?? 0),
    version: String(item?.version ?? ''),
    tags,
    tagSearchText: getTagSearchText(tags),
    children,
    raw: item,
  };
};

export const mapRolloutTree = (rollouts, iteratee) =>
  Array.isArray(rollouts)
    ? rollouts.map((rollout, index) => {
        const nextRollout = iteratee(rollout, index);

        return {
          ...nextRollout,
          children: mapRolloutTree(nextRollout.children, iteratee),
        };
      })
    : [];

export const flattenRolloutTree = (rollouts) =>
  Array.isArray(rollouts)
    ? rollouts.flatMap((rollout) => [
        rollout,
        ...flattenRolloutTree(rollout.children),
      ])
    : [];

export const getRolloutVersionId = (rolloutFullVersion) => {
  const normalized = String(rolloutFullVersion ?? '');

  if (!normalized) {
    return '';
  }

  return normalized.includes('|') ? normalized.split('|')[1] : normalized;
};

export const getRolloutMajorVersion = (rolloutName) => {
  const normalized = String(rolloutName ?? '');

  if (!normalized.includes('--')) {
    return '0';
  }

  const [, versionPart = '0'] = normalized.split('--');
  const [majorVersion = '0'] = versionPart.split('.');

  return majorVersion || '0';
};
